import { useId, useState } from "react";
import cn from "classnames";
import {
    describe as describeSequence,
    dialSequenceProblem,
    totalSeconds,
    MAX_DIAL_STEPS,
    MAX_WAIT_SECONDS,
    TWILIO_IVR_TIMEOUT_SECONDS,
    WAIT_OPTIONS,
    type DialStep,
} from "@/features/phone-call-widget/dialSequence";
import type { PhoneNumberFormVariant } from "@/features/phone-call-widget/PhoneNumberFormFields";
import AlertTriangleIcon from "@icons/alert-triangle.svg?react";

export interface DialSequenceFieldProps {
    /** Controlled value. Normalised (never a zero-second wait or empty-digits press) — see `emit`. */
    value: DialStep[];
    onChange: (steps: DialStep[]) => void;
    /**
     * Surface styling, same contract as `PhoneNumberFormFields`. Load-bearing, not cosmetic: the
     * dialer widget renders this on `bg-shadow-950` (#181F2A), where the light-variant label colour
     * `text-gray-700` (#374151) contrasts at 1.61:1 — below even WCAG's 3:1 non-text floor, i.e.
     * effectively invisible. Defaults to `light` so the settings modal is unaffected.
     */
    variant?: PhoneNumberFormVariant;
}

// Purely presentational: the point at which the advisory copy appears. The timeout, the code-entry
// margin and the blocking budget derived from them live in dialSequence.ts, next to
// `dialSequenceProblem`, which owns the submit gate.
const ADVISORY_THRESHOLD_SECONDS = 20;

type Mode = "simple" | "expanded";

const WAIT = (seconds: number): DialStep => ({ kind: "wait", seconds });
const PRESS = (digits: string): DialStep => ({ kind: "press", digits });

const waitOptionLabel = (seconds: number) =>
    seconds === 0 ? "Don't wait" : `${seconds} second${seconds === 1 ? "" : "s"}`;

// Dial-pad characters only — matches dialSequence.ts's parse() TOKEN_PATTERN charset, so
// anything a vet types here can round-trip through compile()/parse() cleanly.
const sanitizeDigits = (raw: string): string => raw.replace(/[^0-9*#]/g, "");

/**
 * Determines the entry mode for an initial `value`: simple iff the steps are at most one wait
 * followed by at most one press, in that order — i.e. exactly what the simple-mode UI (one wait
 * dropdown, one dial input) can represent. This is deliberately NOT a length check: a value like
 * `[press, wait, press]` is 3 steps (so a naive "<=2" rule would happen to agree here), but a
 * 2-step `[press, wait]` is also not simple-representable because the order is wrong, and a pure
 * length check would wrongly accept it. Order, not count, is what "simple-representable" means.
 */
const isSimpleRepresentable = (steps: DialStep[]): boolean => {
    if (steps.length > 2) return false;
    if (steps.length < 2) return true;
    return steps[0].kind === "wait" && steps[1].kind === "press";
};

/** Zero-second waits and empty-digits presses are legal local editing state but must never be
 * emitted to the caller: the server validates wait seconds as `.positive()`, so a 0 is a 400. */
const normalize = (steps: DialStep[]): DialStep[] =>
    steps.filter((step) => (step.kind === "wait" ? step.seconds > 0 : step.digits.length > 0));

/** Kind to use for a freshly-added row, alternating from whatever the sequence currently ends on. */
const nextKind = (rows: DialStep[]): DialStep["kind"] => {
    const last = rows[rows.length - 1];
    if (!last) return "wait";
    return last.kind === "wait" ? "press" : "wait";
};

const defaultRowFor = (kind: DialStep["kind"]): DialStep => (kind === "wait" ? WAIT(0) : PRESS(""));

// Safe accessors for simple mode, where rows is guaranteed to be exactly [wait, press] by
// construction (see the useState initializer and the simple-mode handlers below) — avoids an
// `as` cast on a DialStep union at the two call sites that render the simple-mode controls.
const waitSecondsOf = (step: DialStep | undefined): number => (step?.kind === "wait" ? step.seconds : 0);
const pressDigitsOf = (step: DialStep | undefined): string => (step?.kind === "press" ? step.digits : "");

/**
 * The phone-tree step builder (EN-1050): replaces the `,,,,2,101` comma syntax with a small UI
 * over `dialSequence.ts`'s model. Two modes over one state (Design Decision 02, which covers this
 * control — both the two modes and the one-way promotion):
 *
 * - `simple` is the opening state — a "Wait before dialing" dropdown and a "Then dial (optional)"
 *   digits input. This is all most clinics need (wait, then dial an extension).
 * - `expanded` is rows of `[# | Wait|Press | value | remove]` plus an "Add step" control, for
 *   sequences too complex for the two simple controls (e.g. press a menu digit, wait, then dial).
 *
 * Promotion (simple -> expanded) is one-way PER MOUNT: "+ Add another step" carries the simple state
 * into the first two rows and appends a new one, and no control folds back, so within one mount a
 * promoted field stays expanded. That is the honest scope — it is not a property of the value.
 * Delete rows back to a simple-representable shape, then remount (a failed verification followed by
 * Retry does exactly that) and the entry-state rule below opens it in simple mode again.
 *
 * A single `rows: DialStep[]` array backs both modes: simple mode is just a constrained view onto
 * rows[0] (wait) and rows[1] (press) rather than separate state that would need to be reconciled
 * with the row array on promotion.
 *
 * `rows` is seeded once from the initial `value` prop and is the local source of truth after
 * that — it is NOT re-synced from `value` on every render. That's deliberate: emitted values are
 * normalized (zero waits / empty presses stripped, see `normalize`), which is lossy compared to
 * what's being locally edited (e.g. a "Don't wait" selection or a mid-typed empty press input).
 * Re-deriving local state from the parent's normalized `value` would fight the user's typing.
 */
function DialSequenceField({ value, onChange, variant = "light" }: DialSequenceFieldProps) {
    const isLight = variant === "light";

    // Per-instance id namespace. Two of these CAN be in the DOM at once, and it is not hypothetical:
    // opening the dialer while already on Settings puts two DialSequenceFields in the DOM, with no
    // navigation involved at all — a vet with no verified caller ID lands on the dialer's caller-ID
    // form (CallerIdNumberForm, its initial state) while the settings verification modal
    // (CallerIdVerificationModal via CallSettings) is open behind it. With fixed ids the second
    // "Then dial" input would lose its accessible name entirely — its only name is the label->id association, unlike the
    // wait <select>, which also carries an aria-label — and clicking the second label would focus the
    // first form's input. `useId` rather than an `idPrefix` prop so correctness does not depend on
    // every call site remembering to pass one; same pattern as HorizontalScroller and
    // HDKeyedSingleSelect.
    const uid = useId();

    // Dark values mirror PhoneNumberFormFields' labelClass/optionalClass exactly, so the builder and
    // the fields it sits between cannot drift apart. Contrast on bg-shadow-950 (#181F2A):
    // white 16.6:1, gray-400 6.5:1, gray-300 11.24:1 — all pass WCAG AA for body text.
    const labelClass = cn("text-sm font-medium", isLight ? "text-gray-700" : "text-white");
    const optionalClass = cn("font-normal", isLight ? "text-gray-500" : "text-gray-400");
    const stepNumberClass = cn("w-4 text-sm", isLight ? "text-gray-500" : "text-gray-400");
    const previewClass = cn("text-sm", isLight ? "text-gray-600" : "text-gray-300");
    const addStepClass = cn(
        "self-start text-sm font-medium",
        isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-300 hover:text-indigo-200",
    );
    const removeStepClass = cn(
        "px-1.5",
        isLight ? "text-gray-400 hover:text-gray-600" : "text-gray-400 hover:text-white",
    );
    const [mode, setMode] = useState<Mode>(() => (isSimpleRepresentable(value) ? "simple" : "expanded"));
    const [rows, setRows] = useState<DialStep[]>(() => {
        if (isSimpleRepresentable(value)) {
            const waitStep = value.find((s): s is Extract<DialStep, { kind: "wait" }> => s.kind === "wait");
            const pressStep = value.find((s): s is Extract<DialStep, { kind: "press" }> => s.kind === "press");
            return [waitStep ?? WAIT(0), pressStep ?? PRESS("")];
        }
        // isSimpleRepresentable only returns false for steps.length >= 2, so value is non-empty here.
        return value;
    });

    const commit = (nextRows: DialStep[]) => {
        setRows(nextRows);
        onChange(normalize(nextRows));
    };

    const promote = () => {
        const appended = [...rows, defaultRowFor(nextKind(rows))];
        setMode("expanded");
        commit(appended);
    };

    const addStep = () => commit([...rows, defaultRowFor(nextKind(rows))]);

    const removeRow = (index: number) => commit(rows.filter((_, i) => i !== index));

    const setRowKind = (index: number, kind: DialStep["kind"]) =>
        commit(rows.map((row, i) => (i === index ? defaultRowFor(kind) : row)));

    const setRowWaitSeconds = (index: number, seconds: number) =>
        commit(rows.map((row, i) => (i === index ? WAIT(seconds) : row)));

    const setRowDigits = (index: number, digits: string) =>
        commit(rows.map((row, i) => (i === index ? PRESS(sanitizeDigits(digits)) : row)));

    const previewText = describeSequence(rows);

    // The submit gate and the message the vet reads come from the same call, in this component and
    // in both parent forms (see `dialSequenceProblem`). Computed from `rows` rather than `value`
    // because the caps have to bite on what is on screen; `normalize` only ever drops zero-waits
    // and empty presses, neither of which changes the total or any cap, so the two agree anyway.
    const blockingProblem = dialSequenceProblem(rows);

    // No Math.max clamp, on purpose: clamping makes the copy dishonest, since a 40s and a 90s
    // sequence would both render "about 0 seconds". It is safe to leave unclamped because above the
    // budget this block does not render at all — `blockingProblem` does — so the subtraction is only
    // ever evaluated where it is positive.
    const remainingSeconds = Math.round(TWILIO_IVR_TIMEOUT_SECONDS - totalSeconds(rows));
    const showAdvisory = !blockingProblem && totalSeconds(rows) >= ADVISORY_THRESHOLD_SECONDS;

    // A 21st step could never be saved (server: DialStepsSchema.max(20)), so the control that
    // would create it is disabled with the reason on screen rather than left enabled to produce a
    // 400. Seeded rows can still exceed the cap — a legacy comma string is unbounded — which is
    // why `dialSequenceProblem` also checks it and blocks submit.
    const atStepLimit = rows.length >= MAX_DIAL_STEPS;

    const waitOptionSelect = (id: string, ariaLabel: string, seconds: number, onSelect: (seconds: number) => void) => {
        // A <select> whose `value` matches no <option> renders the FIRST option as selected, so an
        // out-of-set wait would silently display as "Don't wait" (0s) while `describe` reported the
        // real duration — and the next change to the row would commit that 0. Legacy comma rows can
        // hold any even value (`parse` is unbounded) and the server accepts up to 120s, so widen the
        // list for the row that actually holds such a value rather than clamping it away.
        // Widened only up to the server's own ceiling: a stored value above MAX_WAIT_SECONDS
        // cannot be saved as-is either way (dialSequenceProblem blocks it and names the step), so
        // offering it as a choosable option would only invite the vet to re-pick an unsaveable
        // value. Below the ceiling the widening still applies, which is the legacy-truthfulness
        // fix it exists for.
        const options =
            WAIT_OPTIONS.includes(seconds) || seconds > MAX_WAIT_SECONDS
                ? WAIT_OPTIONS
                : [...WAIT_OPTIONS, seconds].sort((a, b) => a - b);
        return (
            <select
                id={id}
                aria-label={ariaLabel}
                value={seconds}
                onChange={(e) => onSelect(Number(e.target.value))}
                className="rounded-md border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-900"
            >
                {options.map((option) => (
                    <option key={option} value={option}>
                        {waitOptionLabel(option)}
                    </option>
                ))}
            </select>
        );
    };

    return (
        <div className="flex flex-col gap-3">
            {mode === "simple" ? (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <label htmlFor={`${uid}-wait`} className={labelClass}>
                            Wait before dialing
                        </label>
                        {waitOptionSelect(`${uid}-wait`, "Wait before dialing", waitSecondsOf(rows[0]), (seconds) =>
                            setRowWaitSeconds(0, seconds),
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label htmlFor={`${uid}-press`} className={labelClass}>
                            Then dial <span className={optionalClass}>(optional)</span>
                        </label>
                        <input
                            id={`${uid}-press`}
                            type="text"
                            value={pressDigitsOf(rows[1])}
                            onChange={(e) => setRowDigits(1, e.target.value)}
                            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900"
                        />
                    </div>
                    <button type="button" onClick={promote} className={addStepClass}>
                        + Add another step
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {rows.map((row, i) => (
                        // eslint-disable-next-line react/no-array-index-key -- deliberate, and safe by
                        // enumeration rather than by luck. `DialStep` has no stable id, and the only
                        // mutation that shifts the index->row mapping is `removeRow`; `promote`/`addStep`
                        // append, the setters are same-index in place, and there is no insert-in-middle
                        // and no reorder. Every control here is fully controlled, and React's onChange
                        // fires on every `input` event rather than on blur, so no uncommitted value
                        // exists when the array shifts and values cannot be misattributed. That is the
                        // whole correctness argument; if a future edit adds insert-in-middle or reorder,
                        // it no longer holds and these rows need real ids.
                        <div key={i} className="flex items-center gap-2">
                            <span aria-hidden="true" className={stepNumberClass}>
                                {i + 1}.
                            </span>
                            <select
                                aria-label={`Step ${i + 1} type`}
                                value={row.kind}
                                onChange={(e) => setRowKind(i, e.target.value as DialStep["kind"])}
                                className="rounded-md border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-900"
                            >
                                <option value="wait">Wait</option>
                                <option value="press">Press</option>
                            </select>
                            {row.kind === "wait" ? (
                                waitOptionSelect(
                                    `${uid}-row-${i}-wait`,
                                    `Step ${i + 1} wait seconds`,
                                    row.seconds,
                                    (seconds) => setRowWaitSeconds(i, seconds),
                                )
                            ) : (
                                <input
                                    aria-label={`Step ${i + 1} digits`}
                                    type="text"
                                    value={row.digits}
                                    onChange={(e) => setRowDigits(i, e.target.value)}
                                    className="rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900"
                                />
                            )}
                            <button
                                type="button"
                                aria-label={`Remove step ${i + 1}`}
                                onClick={() => removeRow(i)}
                                className={removeStepClass}
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addStep}
                        disabled={atStepLimit}
                        className={cn(addStepClass, atStepLimit && "opacity-50 cursor-not-allowed")}
                    >
                        + Add step
                    </button>
                    {atStepLimit && (
                        <p data-testid="dial-sequence-step-limit" className={previewClass}>
                            That&rsquo;s the most steps we can save ({MAX_DIAL_STEPS}). Remove one to add another.
                        </p>
                    )}
                </div>
            )}

            {previewText && (
                <p data-testid="dial-sequence-preview" className={previewClass}>
                    {previewText}
                </p>
            )}

            {blockingProblem && (
                <div
                    data-testid="dial-sequence-blocking-error"
                    role="alert"
                    className={cn(
                        "flex items-start gap-2 rounded-md border px-3 py-2.5",
                        isLight
                            ? "border-red-200 bg-red-50 text-red-900"
                            : "border-red-500/30 bg-red-500/10 text-red-300",
                    )}
                >
                    <AlertTriangleIcon
                        className={cn("h-4 w-4 mt-0.5 shrink-0", isLight ? "text-red-600" : "text-red-400")}
                        aria-hidden="true"
                    />
                    <p className="text-sm">{blockingProblem}</p>
                </div>
            )}

            {showAdvisory && (
                <div
                    data-testid="dial-sequence-timeout-advisory"
                    role="status"
                    className={cn(
                        "flex items-start gap-2 rounded-md border px-3 py-2.5",
                        isLight
                            ? "border-tropicana-200 bg-tropicana-25 text-tropicana-900"
                            : "border-tropicana-500/30 bg-tropicana-500/10 text-tropicana-300",
                    )}
                >
                    <AlertTriangleIcon
                        className={cn("h-4 w-4 mt-0.5 shrink-0", isLight ? "text-tropicana-600" : "text-tropicana-400")}
                        aria-hidden="true"
                    />
                    <p className="text-sm">
                        This sequence takes a while — that leaves about <strong>{remainingSeconds} seconds</strong> to
                        enter the code before the call times out. Shorten a wait if verification keeps failing.
                    </p>
                </div>
            )}
        </div>
    );
}

export default DialSequenceField;
